import { useEffect, useRef, useState, type RefObject } from 'react';

interface UseGlobalDropzoneOptions {
  enabled: boolean;
  onDropFiles: (files: File[]) => void;
  onRejected?: () => void;
}

function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

/**
 * Drag-and-drop em qualquer ponto de `ref` (não só numa caixa dedicada), com
 * contador de profundidade — dragenter/dragleave disparam em cada elemento
 * filho sobrevoado, não só uma vez no container.
 */
export function useGlobalDropzone<T extends HTMLElement>(
  ref: RefObject<T | null>,
  { enabled, onDropFiles, onRejected }: UseGlobalDropzoneOptions,
) {
  const [isDragging, setIsDragging] = useState(false);
  const depthRef = useRef(0);

  // "Latest ref" — onDropFiles/onRejected costumam ser closures novas a cada
  // render do chamador. Guardar aqui evita que o efeito abaixo precise
  // re-registrar os listeners de DOM toda vez que o componente pai renderiza
  // por qualquer outro motivo (ex: trocar de coleção selecionada). Atualiza
  // via efeito (não durante o render) — mutar ref em render quebra a
  // suposição de pureza que o React (e o lint react-hooks/refs) exige.
  const onDropFilesRef = useRef(onDropFiles);
  const onRejectedRef = useRef(onRejected);
  useEffect(() => {
    onDropFilesRef.current = onDropFiles;
    onRejectedRef.current = onRejected;
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    function handleDragEnter(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depthRef.current += 1;
      if (!enabled) {
        onRejectedRef.current?.();
        return;
      }
      setIsDragging(true);
    }

    function handleDragOver(e: DragEvent) {
      if (hasFiles(e)) e.preventDefault();
    }

    function handleDragLeave(e: DragEvent) {
      if (!hasFiles(e)) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setIsDragging(false);
    }

    function handleDrop(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depthRef.current = 0;
      setIsDragging(false);
      if (!enabled) {
        onRejectedRef.current?.();
        return;
      }
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) onDropFilesRef.current(files);
    }

    el.addEventListener('dragenter', handleDragEnter);
    el.addEventListener('dragover', handleDragOver);
    el.addEventListener('dragleave', handleDragLeave);
    el.addEventListener('drop', handleDrop);

    return () => {
      el.removeEventListener('dragenter', handleDragEnter);
      el.removeEventListener('dragover', handleDragOver);
      el.removeEventListener('dragleave', handleDragLeave);
      el.removeEventListener('drop', handleDrop);
    };
  }, [ref, enabled]);

  return { isDragging };
}
